import { ISydDocument } from 'models/shared.models'
import { UploadedFile } from 'models/general'
import {
  IAccountingFileType,
  IAccountingStepFiveUploadColumn,
} from 'models/accounting.models'
import { accountingFileTypes } from 'lib/utils/Accounting/accounting.utils'
import {
  IAccountingStepFiveState,
  IUseAccountingStepFive,
} from './useAccountingStepFive'

interface IUseAccountingStepFiveUploadColumnsProps
  extends Pick<
    IUseAccountingStepFive,
    | 'firstCategoryFileStatuses'
    | 'uploadedFirstCategoryFiles'
    | 'firstCategoryFilesToValidate'
    | 'thirdCategoryFileStatuses'
    | 'uploadedFourthCategoryFiles'
  > {
  totalFirstCategoryFiles: ISydDocument[]
  totalSecondCategoryFiles: ISydDocument[]
  totalThirdCategoryFiles: ISydDocument[]
  totalFourthCategoryFiles: ISydDocument[]
  uploadedSecondCategoryFiles: ISydDocument[]
  uploadedThirdCategoryFiles: ISydDocument[]
  thirdCategoryFilesToValidate: ISydDocument[]
  firstCategoryValidFiles: ISydDocument[]
  thirdCategoryValidFiles: ISydDocument[]
  firstCategoryInvalidFiles: ISydDocument[]
  thirdCategoryInvalidFiles: ISydDocument[]
  uploadedAndValidatedFirstCategoryFiles: ISydDocument[]
  uploadedAndValidatedThirdCategoryFiles: ISydDocument[]
  accountingStepFiveFirstFileCategoryType?: IAccountingFileType
  accountingStepFiveSecondFileCategoryType?: IAccountingFileType
  accountingStepFiveThirdFileCategoryType?: IAccountingFileType
  accountingStepFiveFourthFileCategoryType?: IAccountingFileType
  uploadingFirstFile?: UploadedFile
  uploadingSecondFile?: UploadedFile
  uploadingThirdFile?: UploadedFile
  uploadingFourthFile?: UploadedFile
  updateState: (update: (draft: IAccountingStepFiveState) => void) => void
}

interface IUseAccountingStepFiveUploadColumns {
  dynamicFileUploadColumns: IAccountingStepFiveUploadColumn<IAccountingStepFiveState>[]
}

const useAccountingStepFiveUploadColumns = ({
  totalFirstCategoryFiles,
  totalSecondCategoryFiles,
  totalThirdCategoryFiles,
  totalFourthCategoryFiles,
  uploadedSecondCategoryFiles,
  uploadedThirdCategoryFiles,
  thirdCategoryFilesToValidate,
  firstCategoryValidFiles,
  thirdCategoryValidFiles,
  firstCategoryInvalidFiles,
  thirdCategoryInvalidFiles,
  uploadedAndValidatedFirstCategoryFiles,
  uploadedAndValidatedThirdCategoryFiles,
  firstCategoryFileStatuses,
  uploadedFirstCategoryFiles,
  firstCategoryFilesToValidate,
  thirdCategoryFileStatuses,
  uploadedFourthCategoryFiles,
  accountingStepFiveFirstFileCategoryType,
  accountingStepFiveSecondFileCategoryType,
  accountingStepFiveThirdFileCategoryType,
  accountingStepFiveFourthFileCategoryType,
  uploadingFirstFile,
  uploadingSecondFile,
  uploadingThirdFile,
  uploadingFourthFile,
  updateState,
}: IUseAccountingStepFiveUploadColumnsProps): IUseAccountingStepFiveUploadColumns => {
  const dynamicFileUploadColumns: IAccountingStepFiveUploadColumn<IAccountingStepFiveState>[] =
    [
      {
        id: accountingFileTypes.provisionalAccountingNotification,
        label:
          'Acuse de contribuciones de recibo de declaración provisional o definitiva de impuestos federales (Contador)',
        disabled: false,
        totalUploadItems: totalFirstCategoryFiles,
        uploadedFiles: uploadedFirstCategoryFiles,
        fileType: accountingStepFiveFirstFileCategoryType?.type,
        fileStatuses: firstCategoryFileStatuses,
        fileStatusesKey: 'firstCategoryFileStatuses',
        uploadingFile: uploadingFirstFile,
        filesToValidate: firstCategoryFilesToValidate,
        uploadedAndValidatedFiles: uploadedAndValidatedFirstCategoryFiles,
        additionalValidFiles: thirdCategoryValidFiles,
        additionalInvalidFiles: thirdCategoryInvalidFiles,
        updateState,
      },
      {
        id: accountingFileTypes.paymentReceipt,
        label: 'Comprobante de Pago (Cliente)',
        disabled: !uploadedAndValidatedFirstCategoryFiles.length,
        totalUploadItems: totalSecondCategoryFiles,
        uploadedFiles: uploadedSecondCategoryFiles,
        fileType: accountingStepFiveSecondFileCategoryType?.type,
        uploadingFile: uploadingSecondFile,
        additionalValidFiles: [
          ...firstCategoryValidFiles,
          ...thirdCategoryValidFiles,
        ],
        additionalInvalidFiles: [
          ...firstCategoryInvalidFiles,
          ...thirdCategoryInvalidFiles,
        ],
        updateState,
      },
      {
        id: accountingFileTypes.taxPaymentNotification,
        label: 'Acuse de pago del SAT (Contador)',
        disabled: !uploadedAndValidatedFirstCategoryFiles.length,
        totalUploadItems: totalThirdCategoryFiles,
        uploadedFiles: uploadedThirdCategoryFiles,
        fileType: accountingStepFiveThirdFileCategoryType?.type,
        fileStatuses: thirdCategoryFileStatuses,
        fileStatusesKey: 'thirdCategoryFileStatuses',
        uploadingFile: uploadingThirdFile,
        filesToValidate: thirdCategoryFilesToValidate,
        uploadedAndValidatedFiles: uploadedAndValidatedThirdCategoryFiles,
        additionalValidFiles: firstCategoryValidFiles,
        additionalInvalidFiles: firstCategoryInvalidFiles,
        updateState,
      },
      {
        id: accountingFileTypes.additionalDocuments,
        label: 'Documentos adicionales (Cliente o contador)',
        disabled: false,
        totalUploadItems: totalFourthCategoryFiles,
        uploadedFiles: uploadedFourthCategoryFiles,
        fileType: accountingStepFiveFourthFileCategoryType?.type,
        uploadingFile: uploadingFourthFile,
        additionalValidFiles: [
          ...firstCategoryValidFiles,
          ...thirdCategoryValidFiles,
        ],
        additionalInvalidFiles: [
          ...firstCategoryInvalidFiles,
          ...thirdCategoryInvalidFiles,
        ],
        updateState,
      },
    ]

  return {
    dynamicFileUploadColumns,
  }
}

export default useAccountingStepFiveUploadColumns
